import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { LoadingComponent } from './shared/components/loading/loading.component';
import { AppInsightsService } from 'src/app/core/services/app-insights.service';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { filter, switchMap, take } from 'rxjs/operators';
import { PwaUpdateService } from './core/services/pwa-update.service';
import { PwaInstallService } from './core/services/pwa-install.service';
import { PwaInstallBannerComponent } from './shared/components/pwa-install-banner/pwa-install-banner.component';
import { PwaStatusComponent } from './shared/components/pwa-status/pwa-status.component';
import { NativeAuthService } from './core/auth/native-auth.service';
import { NativePushService } from './core/services/native-push.service';
import { AppBadgeService } from './core/services/app-badge.service';
import { StartupTelemetryService } from './core/services/startup-telemetry.service';
import { isLoginRequiredError, isInteractiveReauthRequiredError } from './core/auth/reauth-error';
import { initiateInteractiveReauth } from './core/auth/interactive-reauth';
import { UnicornRecoveryOutcomeService } from './core/services/unicorn-recovery-outcome.service';
import { ErrorScreenTelemetryService } from './core/services/error-screen-telemetry.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    LoadingComponent,
    MatSnackBarModule,
    PwaInstallBannerComponent,
    PwaStatusComponent
],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'rettX';
  private auth0 = inject(AuthService);
  private router = inject(Router);
  private appInsightsService = inject(AppInsightsService);
  private pwaUpdateService = inject(PwaUpdateService);
  private pwaInstallService = inject(PwaInstallService);
  private nativeAuthService = inject(NativeAuthService);
  private nativePushService = inject(NativePushService);
  private appBadgeService = inject(AppBadgeService);
  private startupTelemetryService = inject(StartupTelemetryService);
  private unicornRecoveryOutcome = inject(UnicornRecoveryOutcomeService);
  private errorScreenTelemetry = inject(ErrorScreenTelemetryService);

  ngOnInit() {
    // Emit startup + auth-lifecycle breadcrumbs (spec 034) so the cold-start
    // tokenless window becomes visible in App Insights. Observability only —
    // never gates a request or changes the auth flow. Started first so the
    // `startup_begin` breadcrumb is recorded before anything else can hang.
    this.startupTelemetryService.init();

    // Initialize PWA services by injecting them (they initialize in constructor)
    // This ensures they start monitoring for updates and install prompts

    // On the native Capacitor shell, wire up the Auth0 custom-scheme callback
    // listener. No-op on the web (the standard /callback route handles it).
    this.nativeAuthService.init();

    // On the native Capacitor shell, wire up the FCM push listeners (token
    // registration, foreground badge refresh, tap deep-link into /messages/:id).
    // No-op on the web, where the browser Web Push/VAPID path is used instead.
    void this.nativePushService.init();

    // On the native Capacitor shell, mirror the unread Message Center count
    // onto the OS app-icon badge (frontend half of rettx #28). No-op on the web.
    this.appBadgeService.init();
    
    // Handle Auth0 errors that require re-authentication.
    // `missing_refresh_token` / `invalid_grant` = broken-but-authenticated
    // session -> force a real interactive re-login (breaks the /login loop).
    // `login_required` = unauthenticated -> navigate to `/login` (public
    // landing), unchanged.
    this.auth0.error$.subscribe((error) => {
      const err = error as any;
      if (isInteractiveReauthRequiredError(err)) {
        console.warn('[Auth0 error$] Broken refresh-token session, forcing interactive re-login...');
        initiateInteractiveReauth(this.auth0);
      } else if (isLoginRequiredError(err)) {
        console.warn('[Auth0 error$] Login required, redirecting to landing page...');
        const returnUrl = this.router.url;
        this.router.navigate(['/login'], {
          queryParams: returnUrl !== '/' ? { returnUrl } : {}
        });
      } else {
        console.error('[Auth0 error$]', err);
      }
    });

    // Log navigation events for debugging
    this.router.events.pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.unicornRecoveryOutcome.recordNavigation(event.urlAfterRedirects);
        // Spec 051 Phase 2 (FR-012): resolve any pending error-screen recovery
        // against this landing and emit its outcome. Separate sessionStorage
        // key from the unicorn path above, so the two cannot cross-resolve.
        this.errorScreenTelemetry.resolveRecoveryOutcome(event.urlAfterRedirects);
      });

    // Note: Authentication redirects are now handled by CustomAuthGuard on protected routes
    // This prevents race conditions during Auth0 callback processing
  }
}
